
import { useState, useRef, useCallback, useEffect } from 'react';

export interface Particle {
  x: number;
  y: number;
  radius: number;
  opacity: number;
  velocityX: number;
  velocityY: number;
  color: string;
}

export interface ParticleConfig {
  maxParticles: number;
  particleSize: {
    min: number;
    max: number;
  };
  fadeSpeed: number;
  velocityRange: number;
  colors: string[];
  trailMode: 'follow' | 'burst' | 'gentle';
  lifespan: number;
}

const defaultConfig: ParticleConfig = {
  maxParticles: 15,
  particleSize: {
    min: 0.5,
    max: 2,
  },
  fadeSpeed: 0.02,
  velocityRange: 0.5,
  colors: ['rgba(255, 255, 255, 0.7)'],
  trailMode: 'follow',
  lifespan: 1, // multiplier for fadeSpeed
};

export const useParticleSystem = (customConfig: Partial<ParticleConfig> = {}) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isMounted = useRef(false);
  
  // Merge default config with custom config
  const config = {
    ...defaultConfig,
    ...customConfig,
    particleSize: {
      ...defaultConfig.particleSize,
      ...(customConfig.particleSize || {}),
    }
  };

  const createParticle = useCallback((x: number, y: number): Particle => {
    const randomColor = config.colors[Math.floor(Math.random() * config.colors.length)];
    const velocityMultiplier = config.trailMode === 'burst' ? 2 : 1;
    const radiusRange = config.particleSize.max - config.particleSize.min;
    
    return {
      x,
      y,
      radius: config.particleSize.min + Math.random() * radiusRange,
      opacity: config.trailMode === 'gentle' ? 0.5 : 0.7,
      velocityX: (Math.random() - 0.5) * config.velocityRange * velocityMultiplier,
      velocityY: (Math.random() - 0.5) * config.velocityRange * velocityMultiplier,
      color: randomColor
    };
  }, [config]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const newPosition = { x: e.clientX, y: e.clientY };
    setPosition(newPosition);

    // Add new particles based on trail mode
    if (particles.length < config.maxParticles) {
      if (config.trailMode === 'burst') {
        // Burst mode: create multiple particles at once
        const burstCount = Math.min(3, config.maxParticles - particles.length);
        const newParticles = Array.from({ length: burstCount }, () => 
          createParticle(newPosition.x, newPosition.y)
        );
        setParticles(prev => [...prev, ...newParticles]);
      } else {
        // Follow/gentle modes: create one particle at a time
        setParticles(prev => [...prev, createParticle(newPosition.x, newPosition.y)]);
      }
    }
  }, [createParticle, particles.length, config]);

  const updateAndRenderParticles = useCallback(() => {
    if (!canvasRef.current || !isMounted.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const updatedParticles = particles.map(particle => {
      const newParticle = { ...particle };
      newParticle.x += newParticle.velocityX;
      newParticle.y += newParticle.velocityY;
      newParticle.opacity -= config.fadeSpeed * config.lifespan;
      return newParticle;
    }).filter(particle => particle.opacity > 0);

    updatedParticles.forEach(particle => {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
      
      // Use the particle's color (with opacity)
      const color = particle.color.replace(/[\d.]+\)$/, `${particle.opacity})`);
      ctx.fillStyle = color;
      ctx.fill();
    });

    setParticles(updatedParticles);
    animationFrameRef.current = requestAnimationFrame(updateAndRenderParticles);
  }, [particles, config]);

  useEffect(() => {
    isMounted.current = true;
    
    // Handle window resize
    const handleResize = () => {
      if (canvasRef.current) {
        canvasRef.current.width = window.innerWidth;
        canvasRef.current.height = window.innerHeight;
      }
    };
    
    // Initialize
    handleResize();
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      isMounted.current = false;
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [handleMouseMove]);

  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(updateAndRenderParticles);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [updateAndRenderParticles]);

  return { canvasRef, position, particles };
};
